import React from "react";
import Game from "./Game";
import { render, getConfig, CONTENT_TYPE, shuffle, initErrorMonitoring } from "@educaplay/core";
import { assetsUrl, GameProvider } from "@educaplay/core";
import { initialize } from "@educaplay/store";
import {
  CARD_CORRECTIONS,
  CARD_TYPES,
  LINE_UP_SOUND_PATHS,
} from "./utils/consts";

const gameImage = assetsUrl("LINE_UP/logoActivity.png");
const container = document.getElementById("root");

import ResultsScreen from "./resultComponents/ResultsScreen";

initErrorMonitoring("https://4688534990bf343149a0b681bbd83c0d@o4509038211104768.ingest.de.sentry.io/4509161643966544");

// Not allow ordered arrays by luck
function fyShuffle(arr) {
  const array = arr.map(it => it);
  if (array.length === 1) return array;
  while (isOrdered(array)) {
    shuffle(array);
  }
  return array;
}

function isOrdered(array) {
  if (array.length === 1) return true;
  let ordered = true;

  for (let i = 0; i < array.length - 1; i++) {
    if (array[i].id >= array[i + 1].id) {
      ordered = false;
    }
  }

  return ordered;
}

function getType(item) {
  switch (item.MULTIMEDIA_TIPO) {
    case undefined: // CONTENT_TYPE.TEXT
      return CARD_TYPES.TEXT;

    case CONTENT_TYPE.IMAGE:
      if (item.RESPUESTA && item.RESPUESTA.trim() !== "") {
        return CARD_TYPES.IMAGETEXT;
      } else {
        return CARD_TYPES.IMAGE;
      }

    case CONTENT_TYPE.AUDIO:
      if (item.RESPUESTA && item.RESPUESTA.trim() !== "") {
        return CARD_TYPES.AUDIOTEXT;
      } else {
        return CARD_TYPES.AUDIO;
      }

    default:
      return CARD_TYPES.TEXT; // choose this case or the undefined one
  }
}

async function init() {
  const { data, texts, settings, user, title, description, author, id, url } =
    await getConfig().catch((err) =>
      console.error("Error al obtener la configuración: ", err)
    );

  const { resources } = settings;

  const filteredQuestions = data.PREGUNTAS.filter((question) => {
    // Remove empty cards
    question.RESPUESTAS = question.RESPUESTAS.filter((card) => {
      return !(
        card.RESPUESTA?.trim() === "" &&
        card.MULTIMEDIA === undefined &&
        card.MULTIMEDIA_TIPO === undefined
      );
    });

    // Remove empty questions
    return question.RESPUESTAS.length !== 0;
  }).map((question, index) => ({
    // Add indexes to questions
    ...question,
    id: index,
  }));

  filteredQuestions.forEach((question) => {
    const cards = question.RESPUESTAS.map((item, idx) => ({
      data: {
        src: `${resources}${item.MULTIMEDIA}`,
        type: getType(item),
        text: item.RESPUESTA,
      },

      // dnd-kit's ids' start at 1
      id: idx + 1,
    }));

    // Add in correct order, then add shuffled
    // We need to get new indexes for the spawn animation
    question.correctAnswers = cards;

  });

  const gameTimeLimit = Number(data.TIEMPO);
  const questionTime = 0;
  // Number.isNaN(questionTimeNumber)
  //   ? 0
  //   : questionTimeNumber;

  const formattedQuestions = filteredQuestions.map((question) => {
    return {
      bank:
        question.ENUNCIADO?.trim() != "" || question.MULTIMEDIA
          ? {
              text: question.ENUNCIADO,
              source:
                question.MULTIMEDIA_TIPO === CONTENT_TYPE.YOUTUBE
                  ? question.MULTIMEDIA
                  : `${resources}${question.MULTIMEDIA}`,
              type: question.MULTIMEDIA_TIPO ?? CONTENT_TYPE.TEXT,
            }
          : null,
      checkAnswer: (answer) => {
        // Game checks answers already because it needs to know what is wrong
        return (
          answer &&
          !answer.some(
            (elem) =>
              elem.correction === CARD_CORRECTIONS.uncheked ||
              elem.correction === CARD_CORRECTIONS.wrong
          )
        );
      },
      calculatePoints: (answer, { tries = [] }) => {
        // Count correct cards, exclude already checked ones
        const right = answer.filter(
          (elem) => elem.correction === CARD_CORRECTIONS.ok && !elem.locked
        ).length;

        const questionPoints = (
          (right / question.RESPUESTAS.length) *
          (100 / filteredQuestions.length)
        );

        if (tries.length < 3) return questionPoints / 2 ** tries.length; // 1, 0.5, 0.25

        return 0;
      },
      answer: null,
      points: 100 / filteredQuestions.length,
      time: questionTime,
      id: question.id,
      correctAnswers: question.correctAnswers, // unshuffled
      items: question.items, // shuffled + new index
    };
  });

  const restartGameHandler = () => {
    const shuff = formattedQuestions.map(question => {
      return { ...question, items:fyShuffle(question.correctAnswers) };
    });

    return shuff;
  };

  initialize({
    lives: data.NUMERO_INTENTOS,
    liveActived: data.NUMERO_INTENTOS_INFINITOS !== "si",
    hasCountDown: data.MAXIMO === "si",
    globalCountdown: gameTimeLimit ?? 0,
    randomized: data.ALEATORIO === "si",
    showSolutions: data.OCULTAR_RESPUESTAS !== "1",
    showRestart: data.OCULTAR_REINICIAR !== "1",
    showSocialMedia: data.OCULTAR_REDES !== "1",
    finalText: data.TEXTO_FINAL,
    hasQuestionBank: true,
    hasProgressBar: { view: true, onlyCorrect: true },
    questions: formattedQuestions,
    hasInfinityTries: true,
    settings,
    refreshQuestions: restartGameHandler,
    semiCorrect: true,
    title,
    description,
    author,
    id,
    url,
    user,
    customizations: {
      customLogo: data.LOGO,
      customBarLogo: data.FRANJA,
      backgroundImage: { src: data.BACKGROUND, showGame: true },
      colors: {
        primary: data.COLOR_FONDO,
        text: data.COLOR_FUENTE,
        action: data.COLOR_BOTONES,
        textAction: data.COLOR_FUENTE_B,
      },
    },
    countFooterSpace: true,
  });

  render(
    container,
    <React.StrictMode>
      <GameProvider
        sounds={LINE_UP_SOUND_PATHS}
        gameImage={gameImage}
        texts={texts}
        Game={Game}
        mappers={{
          report: (question) => {
            const isCorrect = (leAnswer) =>
              !leAnswer && leAnswer === ""
                ? false
                : question.checkAnswer(leAnswer);

            const formatAnswer = (answer) => {
              if (!answer) return [];

              let res = "";

              answer.forEach(
                (card) => (res += res === "" ? card.id : ":" + card.id)
              );

              return res;
            };

            const arr = question.tries.map((it) => ({
              s: false,
              i: question.id,
              a: formatAnswer(it),
            }));

            if (isCorrect(question.answer)) {
              arr.push({
                s: true,
                i: question.id,
                a: formatAnswer(question.answer),
              });
            }

            return arr;
          },
        }}
        renders={{
          results: (questions) => {
            // Core answer components go brrrr
            return (
              <ResultsScreen
                questions={questions}
              />
            );
          },
        }}
      />
    </React.StrictMode>
  );
}

init();
